import { DEFAULT_EMAIL_BODY_AFTER_LINK } from '@common/constants';
import type { ProspectPracticeArea } from '@common/enums';
import { getPracticeAreaLabel } from '@common/util';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
    Box,
    Divider,
    FormControl,
    FormHelperText,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    type SelectChangeEvent,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { type Dispatch, type SetStateAction, useEffect, useId, useMemo } from 'react';
import AutoCompleteMultiSelect from '@/components/AutoCompleteMultiselect';
import NewProspectStep from '@/components/NewProspectModal/NewProspectStep';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { useLawFirm } from '@/queries/lawFirms';
import { useTemplates } from '@/queries/templates';
import { useUser, useUsers } from '@/queries/users';
import { getQuoteEmailTemplate } from '@/services/EmailService';
import { theme } from '@/theme';
import { convertToHtml } from '@/utils';
import {
    ALL_PROSPECT_PRACTICE_AREAS,
    getPracticeAreaMatterTypeOptions,
} from '@/utils/practiceAreaMatterTypes';
import type { FormData, QuoteSentErrors } from './QuoteSentModal';

interface Props {
    formData: FormData;
    setFormData: Dispatch<SetStateAction<FormData>>;
    errors: QuoteSentErrors;
    setErrors: Dispatch<SetStateAction<QuoteSentErrors>>;
}

export default function QuoteLinkSentBody({ formData, setFormData, errors, setErrors }: Props) {
    const { activeLawFirmId } = useLawFirmContext();
    const { data: activeLawFirm } = useLawFirm(activeLawFirmId);
    const { data: users = [] } = useUsers({ lawFirmId: activeLawFirmId });
    const { data: assignedLawyer } = useUser(
        formData.assignedLawyer ? Number(formData.assignedLawyer) : null,
    );

    const { data: quoteTemplates = [] } = useTemplates(activeLawFirmId);
    const lawFirmId = activeLawFirmId;
    const isTestFirm = activeLawFirm?.isTestFirm ?? false;

    const practiceAreaMatterTypeOptions = getPracticeAreaMatterTypeOptions({
        lawFirmId,
        isTestFirm,
    });
    const matterTypeOptions = formData.practiceArea
        ? practiceAreaMatterTypeOptions[formData.practiceArea as ProspectPracticeArea]
        : [];

    const practiceAreaSelectId = useId();
    const matterTypeSelectId = useId();
    const selectedTemplateSelectId = useId();

    const filteredQuoteTemplates = useMemo(
        () =>
            quoteTemplates.filter(
                (template) =>
                    template.templateType === 'QuoteIntake' &&
                    template.process === formData.matterType &&
                    template.templateForms.some(
                        (tf) => tf.templateFormType === 'QuoteForm' && tf.status === 'Completed',
                    ),
            ),
        [quoteTemplates, formData.matterType],
    );

    const handleChange = (key: string, value: unknown) => {
        setFormData((prev) => ({ ...prev, [key]: value }));

        if (errors?.[key as keyof QuoteSentErrors]) {
            setErrors((prev) => ({ ...prev, [key]: '' }));
        }
    };

    useEffect(() => {
        if (!formData.practiceArea) {
            handleChange('practiceArea', 'RealEstate');
        }
    }, []);

    useEffect(() => {
        if (!formData.assignedLawyer || !Array.isArray(formData.ccPerson)) return;

        const assignedLawyerEmail = users.find(
            (user) => user.userId === formData.assignedLawyer,
        )?.email;

        if (!assignedLawyerEmail) return;

        const updatedCcPerson = formData.ccPerson.filter((email) => email !== assignedLawyerEmail);

        if (updatedCcPerson.length !== formData.ccPerson.length) {
            handleChange('ccPerson', updatedCcPerson);
        }
    }, [formData.assignedLawyer, users]);

    useEffect(() => {
        if (!assignedLawyer) return;

        setFormData((prev) => ({
            ...prev,
            signature: assignedLawyer.signature ?? '',
        }));
    }, [assignedLawyer, setFormData]);

    const availableCcContacts = users
        ?.filter((user) => user.userId !== formData.assignedLawyer)
        ?.map((user) => user.email);

    const fetchQuoteTemplateCC = async () => {
        try {
            const response = await getQuoteEmailTemplate(formData?.selectedTemplateId);
            setFormData((prev) => ({
                ...prev,
                ccPerson: response.data.emailCC ?? [],
            }));
        } catch (error) {
            console.error('Error fetching quote template:', error);
        }
    };

    useEffect(() => {
        if (formData?.selectedTemplateId) {
            fetchQuoteTemplateCC();
        }
    }, [formData?.selectedTemplateId]);

    const resetError = () => {
        setErrors({
            practiceArea: '',
            matterType: '',
            selectedTemplateId: '',
            clientFirstName: '',
            assignedLawyer: '',
            clientLastName: '',
            clientEmail: '',
        });
    };

    const handleMatterTypeChange = (value: string) => {
        setFormData((prev) => ({
            ...prev,
            matterType: value,
            selectedTemplateId: '',
            assignedLawyer: 0,
            lawyerName: '',
            lawyerEmail: '',
            quoteFormLink: '',
            ccPerson: [],
        }));
        resetError();
    };

    const handleTemplateChange = (templateId: number) => {
        const selectedTemplate = filteredQuoteTemplates.find(
            (template) => template.quoteTemplateId === templateId,
        );

        const quoteFormObj = selectedTemplate?.templateForms?.find(
            (element) => element?.templateFormType === 'QuoteForm',
        );
        const quoteTemplateCode = quoteFormObj?.templateFormCode;
        const quoteFormLink = quoteTemplateCode
            ? `${window.location.origin}/q/${quoteTemplateCode}`
            : '';

        setFormData((prev) => ({
            ...prev,
            selectedTemplateId: String(templateId),
            assignedLawyer: selectedTemplate?.quoteApproverId ?? 0,
            lawyerName: selectedTemplate?.quoteApprover?.fullName ?? '',
            lawyerEmail: selectedTemplate?.quoteApprover?.email ?? '',
            quoteFormLink,
            ccPerson: [],
        }));
        resetError();
    };

    const isBasicInfoCompleted =
        formData.assignedLawyer &&
        formData.selectedTemplateId &&
        formData.practiceArea &&
        formData.matterType;

    const isClientInfoCompleted =
        formData.clientFirstName && formData.clientLastName && formData.clientEmail;

    return (
        <Stack gap={3}>
            <NewProspectStep
                stepNumber="1"
                title="Initial Details"
                description="Set up basic case information and assignments"
                isComplete={!!isBasicInfoCompleted}
            >
                <Stack spacing={4}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <FormControl fullWidth required error={!!errors?.practiceArea}>
                            <InputLabel id={practiceAreaSelectId}>Practice Area</InputLabel>
                            <Select
                                labelId={practiceAreaSelectId}
                                label="Practice Area"
                                value={formData.practiceArea}
                                sx={{ backgroundColor: 'white' }}
                                onChange={(event: SelectChangeEvent<string>) => {
                                    handleChange('practiceArea', event.target.value);
                                    resetError();
                                }}
                            >
                                {ALL_PROSPECT_PRACTICE_AREAS.map((option) => (
                                    <MenuItem
                                        key={option}
                                        value={option}
                                        disabled={option !== 'RealEstate'}
                                    >
                                        {getPracticeAreaLabel(option)}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText sx={{ color: 'error.main' }}>
                                {errors?.practiceArea}
                            </FormHelperText>
                        </FormControl>

                        <ArrowForwardIcon color="action" />

                        <FormControl fullWidth required error={!!errors?.matterType}>
                            <InputLabel id={matterTypeSelectId}>Matter Type</InputLabel>
                            <Select
                                labelId={matterTypeSelectId}
                                label="Matter Type"
                                value={formData.matterType}
                                disabled={!formData.practiceArea}
                                sx={{ backgroundColor: 'white' }}
                                onChange={(event: SelectChangeEvent<string>) =>
                                    handleMatterTypeChange(event.target.value)
                                }
                            >
                                {matterTypeOptions.map(({ label, value, disabled }) => (
                                    <MenuItem key={value} value={value} disabled={disabled}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText sx={{ color: 'error.main' }}>
                                {errors?.matterType}
                            </FormHelperText>
                        </FormControl>

                        <ArrowForwardIcon color="action" />

                        <Tooltip
                            title={!formData.matterType ? 'Please select matter type first' : ''}
                            placement="top"
                        >
                            <FormControl fullWidth required error={!!errors?.selectedTemplateId}>
                                <InputLabel id={selectedTemplateSelectId}>
                                    Select Template
                                </InputLabel>
                                <Select
                                    labelId={selectedTemplateSelectId}
                                    label="Select Template"
                                    value={formData.selectedTemplateId || ''}
                                    disabled={!formData.matterType}
                                    sx={{ backgroundColor: 'white' }}
                                    onChange={(event: SelectChangeEvent<string>) => {
                                        handleTemplateChange(Number(event.target.value));
                                    }}
                                >
                                    {filteredQuoteTemplates.map((template) => (
                                        <MenuItem
                                            key={template.quoteTemplateId}
                                            value={template.quoteTemplateId}
                                        >
                                            {template.quoteTemplateName}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText sx={{ color: 'error.main' }}>
                                    {errors?.selectedTemplateId}
                                </FormHelperText>
                            </FormControl>
                        </Tooltip>
                    </Stack>

                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Tooltip
                            title="To make changes to this field, please update it in the selected template."
                            placement="top"
                        >
                            <TextField
                                fullWidth
                                required
                                disabled
                                label="Assigned User"
                                value={formData.lawyerName}
                                placeholder="Select Assigned User"
                                error={!!errors?.assignedLawyer}
                                helperText={errors?.assignedLawyer}
                                sx={{
                                    backgroundColor: 'white',
                                    width: 'calc(33% - 6px)',
                                }}
                            />
                        </Tooltip>

                        <Tooltip
                            title="To make changes to this field, please update it in the selected template."
                            placement="top"
                        >
                            <FormControl sx={{ width: 'calc(67% - 6px)' }}>
                                <AutoCompleteMultiSelect
                                    fullWidth
                                    disabled
                                    options={availableCcContacts}
                                    getOptionLabel={(option) => option}
                                    label="CC List"
                                    value={formData?.ccPerson}
                                    onChange={(_event, value) => {
                                        handleChange('ccPerson', value);
                                    }}
                                    sx={{ backgroundColor: 'white' }}
                                />
                            </FormControl>
                        </Tooltip>
                    </Stack>
                </Stack>
            </NewProspectStep>

            <NewProspectStep
                stepNumber="2"
                title="Client Information"
                description="Add primary client and additional parties"
                isComplete={!!isClientInfoCompleted}
            >
                <Stack>
                    <Typography variant="body2" fontWeight={600} color="#202124" mb={1.5}>
                        Primary Client Details
                    </Typography>
                    <Stack direction="row" gap={1.5}>
                        <TextField
                            fullWidth
                            required
                            type="text"
                            label="First Name"
                            placeholder="Enter first name"
                            value={formData.clientFirstName}
                            name="clientFirstName"
                            error={!!errors?.clientFirstName}
                            helperText={errors?.clientFirstName}
                            sx={{ backgroundColor: 'white' }}
                            onChange={(event) => {
                                handleChange('clientFirstName', event.target.value);
                                resetError();
                            }}
                        />
                        <TextField
                            fullWidth
                            required
                            type="text"
                            label="Last Name"
                            placeholder="Enter last name"
                            value={formData.clientLastName}
                            name="clientLastName"
                            error={!!errors?.clientLastName}
                            helperText={errors?.clientLastName}
                            sx={{ backgroundColor: 'white' }}
                            onChange={(event) => {
                                handleChange('clientLastName', event.target.value);
                                resetError();
                            }}
                        />
                        <TextField
                            fullWidth
                            required
                            type="email"
                            label="Email"
                            placeholder="Enter email address"
                            value={formData.clientEmail}
                            name="clientEmail"
                            error={!!errors?.clientEmail}
                            helperText={errors?.clientEmail}
                            sx={{ backgroundColor: 'white' }}
                            onChange={(event) => {
                                handleChange('clientEmail', event.target.value);
                                resetError();
                            }}
                        />
                    </Stack>
                </Stack>
            </NewProspectStep>

            <NewProspectStep
                stepNumber="3"
                title="Email Configuration"
                description="Configure email settings and notifications"
                showStatus={false}
            >
                <Stack gap={2}>
                    {formData?.selectedTemplateId ? (
                        <Paper
                            variant="outlined"
                            sx={{
                                backgroundColor: theme.palette.common.white,
                                borderColor: theme.palette.neutral.borderColor,
                                borderRadius: 2,
                                overflow: 'hidden',
                            }}
                        >
                            <Stack spacing={0}>
                                <Stack
                                    sx={{
                                        px: 3,
                                        py: 2,
                                        backgroundColor: theme.palette.neutral.N_50,
                                    }}
                                >
                                    <Typography variant="subtitle1" fontWeight={500}>
                                        Quote Email Preview
                                    </Typography>
                                </Stack>
                                <Divider />
                                <Stack spacing={0.5} sx={{ px: 3, py: 2 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Subject
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                        Please complete your client quote form.
                                    </Typography>
                                </Stack>
                                <Divider />
                                <Stack spacing={1} sx={{ px: 3, py: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Hi [client name],</strong>
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        dangerouslySetInnerHTML={{
                                            __html: convertToHtml(
                                                'To serve you quicker, we require the completion of the quote form linked below.',
                                            ),
                                        }}
                                    />
                                    <Typography variant="body2">
                                        <Link href="#">Quote form link</Link>
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        dangerouslySetInnerHTML={{
                                            __html: convertToHtml(DEFAULT_EMAIL_BODY_AFTER_LINK),
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        fontWeight={700}
                                        dangerouslySetInnerHTML={{
                                            __html: convertToHtml(formData?.signature),
                                        }}
                                    />
                                </Stack>
                            </Stack>
                        </Paper>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '200px',
                                background: theme.palette.neutral.N_50,
                                width: '100%',
                                borderRadius: '4px',
                                border: `1px solid ${theme.palette.neutral.borderColor}`,
                            }}
                        >
                            <Typography>Please select a template</Typography>
                        </Box>
                    )}
                </Stack>
            </NewProspectStep>
        </Stack>
    );
}
